import { format, parseISO, isValid } from "date-fns";

// ─── Date / time formatters ───────────────────────────────────────────────────

/** "2024-03-15T09:30:00.000Z" → "09:30:00" */
export const trimTime = (isoTime) =>
  isoTime?.split("T")[1]?.split(".")[0] ?? null;

/** ISO string → formatted datetime string, or null if invalid */
export const formattedTime = (isoTime, formatStr = "yyyy-MM-dd HH:mm:ss") => {
  if (typeof isoTime !== "string") return null;
  const parsed = parseISO(isoTime);
  return isValid(parsed) ? format(parsed, formatStr) : null;
};

/** ISO string → formatted date-only string, or null if invalid */
export const formatDateOnly = (isoTime, formatStr = "yyyy-MM-dd") => {
  if (typeof isoTime !== "string") return null;
  const parsed = parseISO(isoTime);
  return isValid(parsed) ? format(parsed, formatStr) : null;
};

/** "14:30" → "2:30 PM" */
export const formatSlotTime = (timeStr) => {
  if (!timeStr) return "—";
  const [h, m] = timeStr.split(":");
  const d = new Date();
  d.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
  return format(d, "h:mm a");
};

// ─── Domain helpers ───────────────────────────────────────────────────────────

/**
 * Extracts the stop reason from a detailed-shipment response
 * (`api/shipments/detailed-shipment/`): finds the first pickup stop
 * (ordered by stopSequence) and returns its stopReasonCode or stopReasonName.
 */
export const getPickupStopReason = (shipmentData) => {
  const stops = shipmentData?.shipmentStops;
  if (!Array.isArray(stops) || !stops.length) return null;

  const isPickup = (stop) => {
    const type = (stop?.stopType || "").trim().toUpperCase();
    return type === "PICKUP" || type === "PICK_UP" || type === "PICKUP_STOP";
  };

  const sorted = [...stops].sort(
    (a, b) =>
      (a?.stopSequence ?? Number.MAX_SAFE_INTEGER) -
      (b?.stopSequence ?? Number.MAX_SAFE_INTEGER)
  );

  const pickup = sorted.find(isPickup) ?? sorted[0];
  return pickup?.stopReasonCode || pickup?.stopReasonName || null;
};

const isDropTrailerReason = (str) => {
  const s = (str || "").trim().toUpperCase();
  return (
    s === "DT" || s === "DROP TRAILER" || s === "DROPTRAILER" ||
    (s.includes("DROP") && s.includes("TRAILER"))
  );
};

/**
 * Derives Drop Trailer vs Live Load.
 *
 * The authoritative source is the pickup stop reason from the detailed-shipment
 * response (via getPickupStopReason). The appointment record's own type fields
 * (isDropTrailer / requestType / stopReasonCode) can be stale or outright wrong,
 * so they are only used as a best-effort seed when there is no shipment to load.
 *
 * Returns a `resolved` flag: it is false while a shipment detail is still loading
 * (shipmentData null but a shipmentNumber exists), signalling callers to render a
 * neutral placeholder instead of a definitive (and possibly wrong) badge that
 * would flicker once the authoritative type arrives.
 */
export const deriveTypeInfo = (shipmentData, appointmentData) => {
  const pickupStopReason = getPickupStopReason(shipmentData);

  // A shipment detail is on its way and is the source of truth — until it lands,
  // the appointment seed cannot be trusted, so the type is unresolved.
  const shipmentPending = Boolean(appointmentData?.shipmentNumber) && shipmentData == null;

  let isDropTrailer;
  if (pickupStopReason != null) {
    isDropTrailer = isDropTrailerReason(pickupStopReason);
  } else if (appointmentData?.isDropTrailer != null) {
    isDropTrailer = Boolean(appointmentData.isDropTrailer);
  } else {
    const requestType = (appointmentData?.requestType || "")
      .replace("_", " ").replace("-", " ");
    isDropTrailer =
      isDropTrailerReason(requestType) || isDropTrailerReason(appointmentData?.stopReasonCode);
  }

  const resolved = pickupStopReason != null || !shipmentPending;

  return {
    isDropTrailer,
    resolved,
    label: isDropTrailer ? "Drop Trailer" : "Live Load",
  };
};

/**
 * Builds the initial shipments array from appointmentData.
 * Pass null for a blank "add" form.
 */
export const buildInitialShipments = (appointmentData) => {
  const blank = {
    shipmentNumber: "", stopNumber: 1, company: "",
    addressLine1: "", addressLine2: "", city: "", zipCode: "",
    country: "", state: "", carrierName: "", officeCodeCarrier: "",
    officeCodeShipper: "", poNumber: "", saleNumber: "",
    stopReasonCode: "", mustPickUpBy: "", mustDeliverBy: "",
  };
  if (!appointmentData) return [blank];
  return [{
    ...blank,
    shipmentNumber:    appointmentData.shipmentNumber   || "",
    stopNumber:        appointmentData.stopNumber       || 1,
    company:           appointmentData.shipperName      || appointmentData.companyName || appointmentData.company || "",
    addressLine1:      appointmentData.addressLine1     || "",
    addressLine2:      appointmentData.addressLine2     || "",
    city:              appointmentData.city             || "",
    zipCode:           appointmentData.zipCode          || "",
    country:           appointmentData.country          || "",
    state:             appointmentData.state            || "",
    carrierName:       appointmentData.carrierName      || "",
    officeCodeCarrier: appointmentData.carrierOfficeCode|| "",
    officeCodeShipper: appointmentData.shipperOfficeCode|| "",
    poNumber:          appointmentData.poNumber         || "",
    saleNumber:        appointmentData.saleNumber       || "",
    stopReasonCode:    appointmentData.stopReasonCode   || "",
    mustPickUpBy:      appointmentData.pickupDate       || "",
    mustDeliverBy:     appointmentData.deliveryDate     || "",
  }];
};